import { useRef, useState, useEffect, useCallback } from 'react';
import { Box, Center, Spinner, SimpleGrid, Text } from '@chakra-ui/react';
import {
  FacebookShareButton,
  FacebookIcon,
  TwitterShareButton,
  XIcon,
  LinkedinShareButton,
  LinkedinIcon,
  RedditShareButton,
  RedditIcon,
  WhatsappShareButton,
  WhatsappIcon,
  TelegramShareButton,
  TelegramIcon,
  ViberShareButton,
  ViberIcon,
} from 'react-share';
import { toaster } from '@/components/ui/toaster';
import {
  ClipboardIconButton,
  ClipboardInput,
  ClipboardLabel,
  ClipboardRoot,
} from '@/components/ui/clipboard';
import { InputGroup } from '@/components/ui/input-group';
import {
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import api from '@/utils/api';
import { buildPublicUrl } from '@/utils/public-url';
import { Content, DailogProps } from '@/lib/types';

interface Props extends DailogProps {
  content: Content;
  reloadContents(): void;
}

export default function ShareContent({ isOpen, onClose, content, reloadContents }: Props) {
  const [link, setLink] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const hasFetchedLink = useRef(false);

  const generatePublicLink = useCallback(
    async (content: Content) => {
      setIsLoading(true);
      try {
        const res = await api.put(`contents/public/${content.id}`);
        const tinyUrl = res.data.data.data.tiny_url;
        setLink(buildPublicUrl('content', content.reference_id) || tinyUrl);
        reloadContents();
        setTimeout(() => reloadContents(), 3000);
        toaster.create({
          title: 'The public link is generated successfully',
          type: 'success',
        });
      } catch (err: any) {
        toaster.create({
          type: 'error',
          title: err.response?.data?.error || 'Failed to generate public link',
        });
      } finally {
        setIsLoading(false);
      }
    },
    [reloadContents],
  );

  useEffect(() => {
    if (isOpen && !hasFetchedLink.current) {
      const fetchLink = async () => {
        if (content.public_link) {
          setLink(buildPublicUrl('content', content.reference_id) || content.public_link);
        } else {
          await generatePublicLink(content);
        }
      };

      fetchLink();
      hasFetchedLink.current = true;
    }
  }, [isOpen, content, generatePublicLink]);

  return (
    <DialogRoot lazyMount open={isOpen} onOpenChange={onClose}>
      <DialogBackdrop />
      <DialogTrigger />
      <DialogContent>
        <DialogCloseTrigger />
        <DialogHeader>
          <DialogTitle>Share this content</DialogTitle>
        </DialogHeader>
        <DialogBody>
          {isLoading ? (
            <Center py={6}>
              <Spinner size='lg' />
            </Center>
          ) : (
            <Box>
              <Text fontSize='xs' color='fg.muted' textTransform='uppercase' letterSpacing='wider' fontWeight='semibold' mb={3}>
                Share via
              </Text>
              <SimpleGrid columns={7} gap={3} mb={6} justifyItems='center'>
                <FacebookShareButton url={link}>
                  <FacebookIcon size={36} round />
                </FacebookShareButton>
                <TwitterShareButton title={content.name} url={link}>
                  <XIcon size={36} round />
                </TwitterShareButton>
                <LinkedinShareButton url={link}>
                  <LinkedinIcon size={36} round />
                </LinkedinShareButton>
                <RedditShareButton
                  url={link}
                  title={content.name}
                  windowWidth={660}
                  windowHeight={460}
                >
                  <RedditIcon size={36} round />
                </RedditShareButton>
                <WhatsappShareButton url={link} title={content.name} separator=':: '>
                  <WhatsappIcon size={36} round />
                </WhatsappShareButton>
                <TelegramShareButton url={link} title={content.name}>
                  <TelegramIcon size={36} round />
                </TelegramShareButton>
                <ViberShareButton url={link} title={content.name}>
                  <ViberIcon size={36} round />
                </ViberShareButton>
              </SimpleGrid>
              <ClipboardRoot maxW='full' value={link}>
                <ClipboardLabel>Or copy link</ClipboardLabel>
                <InputGroup width='full' endElement={<ClipboardIconButton me='-2' />}>
                  <ClipboardInput />
                </InputGroup>
              </ClipboardRoot>
            </Box>
          )}
        </DialogBody>
      </DialogContent>
    </DialogRoot>
  );
}
